import React, { useRef, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import FilmstripItem from './FilmstripItem'
import useMaskStore from '../../stores/maskStore'

const BottomFilmstrip = ({ 
  groups = [], // Default to empty array
  selectImage,
  onAddClick,
  selectedGroup,
  rightPanelVisible 
}) => {
  const filmstripRef = useRef(null)
  const resetMaskState = useMaskStore(state => state.resetMaskState)

  const handleImageSelect = (group) => {
    // Reset mask state before selecting new image
    resetMaskState()
    selectImage(group)
  }

  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      resetMaskState()
      selectImage(null)
    }
  }

  // Memoize sorted groups to prevent unnecessary re-sorting
  const sortedGroups = useMemo(() => {
    if (!Array.isArray(groups)) return []
    
    return [...groups].sort((a, b) => {
      if (!a || !b) return 0
      
      // First sort by processing status
      const aProcessing = a.versions?.some(v => v?.status === 'processing')
      const bProcessing = b.versions?.some(v => v?.status === 'processing')
      if (aProcessing && !bProcessing) return -1
      if (!aProcessing && bProcessing) return 1
      
      // Then sort by last update time
      const aDate = new Date(a.lastUpdatedAt || 0).getTime()
      const bDate = new Date(b.lastUpdatedAt || 0).getTime()
      return bDate - aDate
    })
  }, [groups])

  // Improved scroll behavior with error handling
  useEffect(() => {
    if (!filmstripRef.current || !selectedGroup?.originalGenerationId) return

    try {
      const selectedThumbnail = filmstripRef.current.querySelector(
        `[data-image-id="${selectedGroup.originalGenerationId}"]`
      )
      
      if (!selectedThumbnail) return

      const filmstripRect = filmstripRef.current.getBoundingClientRect()
      const thumbnailRect = selectedThumbnail.getBoundingClientRect()

      const filmstripCenter = filmstripRect.left + filmstripRect.width / 2
      const thumbnailCenter = thumbnailRect.left + thumbnailRect.width / 2
      const scrollAmount = filmstripRef.current.scrollLeft + (thumbnailCenter - filmstripCenter)

      filmstripRef.current.scrollTo({
        left: scrollAmount,
        behavior: 'smooth'
      })
    } catch (error) {
      console.error('Error scrolling to selected thumbnail:', error)
    }
  }, [selectedGroup?.originalGenerationId, sortedGroups.length])

  return (
    <div 
      ref={filmstripRef}
      className="w-full h-28 bg-gray-900 p-4 overflow-x-auto flex items-center scroll-smooth"
      onClick={handleBackgroundClick}
    >
      <div className="flex items-center gap-2">
        <FilmstripItem 
          key="add-button"
          isAddButton 
          onAddClick={onAddClick} 
        />
        
        {sortedGroups.map((group) => (
          <FilmstripItem 
            key={`group-${group?.groupId}-${group?.versions?.[0]?.status}`}
            group={group}
            selectImage={handleImageSelect}
            isSelected={selectedGroup?.groupId === group?.groupId}
          />
        ))}
      </div>
    </div>
  )
}

BottomFilmstrip.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.shape({
    groupId: PropTypes.string,
    originalGenerationId: PropTypes.string,
    versions: PropTypes.arrayOf(PropTypes.shape({
      status: PropTypes.string
    })),
    lastUpdatedAt: PropTypes.string
  })),
  selectImage: PropTypes.func.isRequired,
  onAddClick: PropTypes.func.isRequired,
  selectedGroup: PropTypes.object,
  rightPanelVisible: PropTypes.bool
}

export default BottomFilmstrip